"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, User, ArrowLeft } from "lucide-react";

function SinglePostContent() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="container py-12">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="h-12 bg-muted/30 rounded-lg animate-pulse" />
          <div className="h-96 bg-muted/30 rounded-xl animate-pulse" />
          <div className="space-y-4">
            <div className="h-4 bg-muted/30 rounded animate-pulse" />
            <div className="h-4 bg-muted/30 rounded animate-pulse w-5/6" />
            <div className="h-4 bg-muted/30 rounded animate-pulse w-4/6" />
          </div>
        </div>
      </div>
    );
  }

  const post = posts[0];

  if (!post) {
    return (
      <div className="container py-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-muted-foreground">Post not found.</p>
          <Link to="/BlogPage" className="mt-4 inline-block">
            <Button variant="outline">
              <ArrowLeft />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <article className="container py-12">
      <div className="max-w-4xl mx-auto">
        <Link to="/BlogPage" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors duration-150">
          <ArrowLeft className="size-4" />
          Back to Blog
        </Link>

        <header className="mb-8 space-y-6">
          <h1 className="font-display text-[48px] leading-[1.2] font-bold tracking-[-0.5px]">
            {post.title}
          </h1>

          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            {post.author && (
              <div className="flex items-center gap-2">
                <Avatar className="size-8">
                  {post.author.avatar?.["48"] && (
                    <AvatarImage src={post.author.avatar["48"]} alt={post.author.name} />
                  )}
                  <AvatarFallback>
                    <User className="size-4" />
                  </AvatarFallback>
                </Avatar>
                <span className="font-medium text-foreground">{post.author.name}</span>
              </div>
            )}

            {post.date && (
              <div className="flex items-center gap-2">
                <Calendar className="size-4" />
                <time dateTime={post.date}>
                  {new Date(post.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </time>
              </div>
            )}
          </div>

          {post.categories && post.categories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {post.categories.map((category, i) => (
                <span
                  key={category.id}
                  data-index={i}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-light text-primary"
                >
                  {category.name}
                </span>
              ))}
            </div>
          )}
        </header>

        {post.featuredImage && (
          <div className="mb-12 rounded-xl overflow-hidden shadow-medium">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="w-full h-auto object-cover"
            />
          </div>
        )}

        <div
          className="prose prose-lg max-w-none mb-12"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        {post.tags && post.tags.length > 0 && (
          <div className="border-t pt-8 mb-12">
            <h3 className="font-display text-sm font-semibold text-muted-foreground mb-4">
              Tags
            </h3>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, i) => (
                <span
                  key={tag.id}
                  data-index={i}
                  className="inline-flex items-center px-3 py-1 rounded-md text-sm bg-secondary-light text-secondary"
                >
                  {tag.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {post.author && (
          <div className="border-t border-b py-8 mb-12">
            <div className="flex items-start gap-4">
              <Avatar className="size-16">
                {post.author.avatar?.["96"] && (
                  <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
                )}
                <AvatarFallback>
                  <User className="size-8" />
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-display text-lg font-semibold mb-1">
                  {post.author.name}
                </h3>
                <p className="text-sm text-muted-foreground">Author</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

function RelatedPostsSection() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-64 bg-muted/30 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="card">
          {post.featuredImage && (
            <div className="overflow-hidden rounded-t-xl">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-48 object-cover"
              />
            </div>
          )}
          <CardHeader>
            <CardTitle className="text-lg line-clamp-2">{post.title}</CardTitle>
          </CardHeader>
          <CardContent>
            {post.excerpt && (
              <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                {post.excerpt.replace(/<[^>]*>/g, "")}
              </p>
            )}
            {post.link && (
              <Link to={post.link}>
                <Button variant="outline" size="sm" className="w-full">
                  Read More
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = wvcClient.getCurrentPostID();

  return (
    <section id="single-post-template-section" className="bg-background">
      <PostsProvider wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
        <SinglePostContent />
      </PostsProvider>

      <div className="bg-dark-section py-16">
        <div className="container">
          <h2 className="font-display text-[36px] leading-[1.25] font-bold tracking-[-0.5px] text-foreground-dark mb-8">
            Related Articles
          </h2>
          <PostsProvider wp_query={{ per_page: 3, order: "desc", orderby: "date", exclude: [currentPostId] }} embeds={["wp:featuredmedia"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "_embedded", "_links.wp:featuredmedia"]}>
            <RelatedPostsSection />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}